// src/oura/refreshJobs.ts
//
// Which stages a card is made of, and what its dialog says (epic #3903).
//
// BUTTONS ARE PER CARD; JOBS ARE PER SOURCE. There is one crawl, one Search
// Console pull, one GA4 pull — not eight independent refreshes. Site Health,
// the issues, the page list, Health by area and AI Readability move together
// because they are the same crawl, and the dialog has to say so rather than
// implying independent things happened.
//
// That is why the card list lives here and not in each screen: a dialog that
// names only the card that was clicked is lying by omission about what is about
// to change, and the only way to keep four screens honest is to give them one
// list to read from.

import type { AeoRerunKind } from "@service/aeo";

export interface RefreshJobSpec {
    /** Stable, and part of the storage key — two families must never resume
     *  each other's job id. */
    key: string;
    /**
     * The stages to re-run, in the contract's own names.
     *
     * ourteam-backend#5059 re-runs exactly five: `finalize`, `crawl`, `gsc`,
     * `ga4`, `pagespeed`. Anything else is refused with 422
     * `stage_not_rerunnable`, and the refusal carries the allowed list — which
     * is what the FE renders rather than second-guessing it. They run IN ORDER
     * and stop at the first failure.
     */
    stages: string[];
    /** Every card this job changes. The dialog prints all of them. */
    cards: string[];
    /** Dialog heading. */
    title: string;
    /** What it does. */
    what: string;
    /** What it does NOT touch — the half people get wrong. */
    keeps: string;
    /** What the card says while it runs. */
    running: string;
    /**
     * What we expect it to be, for copy only. The REAL kind comes back from the
     * backend with the cost, and the dialog quotes that; this is what the card
     * can say before it has asked.
     */
    expected: AeoRerunKind;
}

/** The crawl: one job, every card on the Site screen that reads it (#3905). */
export const CRAWL_REFRESH: RefreshJobSpec = {
    key: "crawl",
    // `crawl` re-reads the site; `finalize` recomputes what is derived from it,
    // so the strengths, weaknesses and recommendations move with the pages
    // rather than lagging a scan behind. In order, stopping at the first
    // failure. `audit_fallback` is deliberately NOT here: it measures nothing,
    // it only labels a crawl that failed, and `_run_crawl` refuses to write a
    // failed crawl in the first place.
    stages: ["crawl", "finalize"],
    cards: ["Site Health", "AI Readability", "What the crawl found", "Health by area", "What a machine can read", "Strengths and weaknesses"],
    title: "Re-read the site?",
    what: "This crawls the site again and recomputes everything that comes from the pages: "
        + "the health score, the crawl counters, the ranked page list and AI readability.",
    keeps: "No AI engines are asked and no questions are run, so the AEO score, the 50 questions "
        + "and the last scan date stay as they are.",
    running: "Reading the site again. This takes about a minute.",
    expected: "remeasure",
};

/** Google Analytics (#3906). */
export const TRAFFIC_REFRESH: RefreshJobSpec = {
    key: "ga4",
    stages: ["ga4"],
    cards: ["Traffic"],
    title: "Pull Google Analytics again?",
    what: "This asks Google Analytics for this property's sessions again and replaces the traffic "
        + "figures with what it returns.",
    keeps: "Nothing else moves: the crawl, the questions and the engine numbers are untouched.",
    running: "Asking Google Analytics again.",
    expected: "remeasure",
};

/** Search Console: one pull, both screens (#3907). */
export const SEARCH_REFRESH: RefreshJobSpec = {
    key: "gsc",
    stages: ["gsc"],
    cards: ["Top searches the 50 don't cover", "Searches customers make about you", "Search performance"],
    title: "Pull Search Console again?",
    what: "This re-reads the last 12 months from Search Console and rebuilds the searches behind "
        + "the 50 questions, on this screen and on Search performance.",
    keeps: "No AI engines are asked. The 50 questions themselves do not change — a queued swap "
        + "still waits for the next scan.",
    running: "Reading Search Console again.",
    expected: "remeasure",
};

/**
 * Recompute over stored answers (#3908) — free, deterministic, no new reading.
 *
 * This is the one that pays for itself: when a backend fix ships, every stored
 * assessment stays wrong until its next scan, and all of these blocks are pure
 * functions of data we already hold.
 */
export const ANALYSE_REFRESH: RefreshJobSpec = {
    key: "finalize",
    stages: ["finalize"],
    cards: ["Sources", "Competitors", "Strengths and weaknesses", "Recommendations on Fixes"],
    title: "Recompute from this scan?",
    what: "This recounts everything from the answers already stored — citations, sources, rivals, "
        + "strengths and recommendations — using today's code.",
    keeps: "No engine is asked anything and nothing is measured again, so the numbers only move if "
        + "the way we count them has changed.",
    running: "Recomputing from the stored answers.",
    expected: "recompute",
};

// NO CONTROL FOR THE ENGINE-DERIVED CARDS.
//
// #3908 asked for two: a free recompute and a paid "ask the engines again".
// Only the first exists. ourteam-backend#5059 re-runs five stages —
// `finalize`, `crawl`, `gsc`, `ga4`, `pagespeed` — and `run_engines` is not
// among them, so there is no way to re-ask the engines short of a whole
// assessment. The AI visibility score, Metrics, Distribution by LLM and the 50
// questions therefore carry no control, and the Rerun assessment button stays
// the honest way to ask for that.

/**
 * Whether the stage re-run exists in this deployment at all.
 *
 * Explicit `=== "true"`, absent ⇒ OFF, the same rule every Oura flag follows.
 * ourteam-backend#5059 is not built yet, so without this the controls would
 * render, 404 on the first press and only then remove themselves — a button
 * whose only possible first outcome is failure. Flag OFF, every screen is
 * pixel-identical to today.
 */
export const STAGE_RERUN_LIVE = import.meta.env.VITE_ENABLE_OURA_STAGE_RERUN === "true";

/** The storage key one job uses for one business. */
export function refreshStorageKey(spec: RefreshJobSpec, businessId: string): string {
    return `oura.rerun.${spec.key}.${businessId}`;
}

/**
 * What a finished run actually cost, in the only terms that are true.
 *
 * `estimatedCostUsd` IS 0.0 FOR EVERY STAGE (ourteam-backend#5059): none of
 * them bills a provider, and the backend deliberately reports zero rather than
 * guessing a dollar figure no invoice would match. So the number cannot decide
 * the wording — `kind` does. Reading "free" off a zero would tell a staffer a
 * crawl of a client's whole site was free, when what it actually spent was
 * quota and bandwidth.
 *
 * A non-zero figure is still rendered, for the day a stage does bill something.
 */
export function refreshOutcomeLabel(
    kind: AeoRerunKind | null | undefined,
    cost: number | null | undefined,
): string | null {
    if (typeof cost === "number" && cost > 0) return `This run cost about $${cost.toFixed(2)}.`;
    if (kind === "recompute") return "Recomputed from data already stored — nothing was measured again.";
    if (kind === "remeasure") return "Measured again — this used API quota and bandwidth, not a billed service.";
    return null;
}

/** What a failed re-run says, and whether the Connectors page is where it is fixed. */
export interface RefreshFailure {
    text: string;
    /** The cause is a data source that is not connected or no longer authorised. */
    needsConnector: boolean;
}

/**
 * The backend's own failure string, made readable (ourteam-backend#5059 sends
 * the raw exception: "RuntimeError: GSC is not connected for this business.").
 *
 * Only strings that name a connection problem are rewritten, because for those
 * the next step is known and is not "try again". Anything else is the
 * backend's sentence with a leading exception class name removed, never
 * replaced: a rewrite here would be the screen inventing a diagnosis.
 */
export function explainRefreshFailure(spec: RefreshJobSpec, raw: string | null | undefined): RefreshFailure | null {
    const said = (raw ?? "").trim();
    if (!said) return null;
    const source = spec.key === "ga4" ? "Google Analytics" : "Search Console";
    const named = spec.key === "ga4" ? /\b(ga4|google analytics)\b/i : /\b(gsc|search console)\b/i;
    if (named.test(said) && /not connected|no connector|not linked/i.test(said)) {
        return {
            text: `${source} is not connected for this business, so there is nothing to pull yet. `
                + "Connect it on the Connectors page, then pull again.",
            needsConnector: true,
        };
    }
    if (/expired|revoked|invalid_grant|unauthori[sz]ed|re-?authori[sz]/i.test(said)) {
        return {
            text: `The ${source} authorization expired or was revoked, so nothing could be pulled. `
                + "Reconnect it on the Connectors page, then pull again.",
            needsConnector: true,
        };
    }
    return { text: said.replace(/^[A-Za-z]+(?:Error|Exception):\s*/, ""), needsConnector: false };
}
